import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { useUserProfile } from "../hooks/useUserProfile";
import { signOut as firebaseSignOut } from "firebase/auth";
import "../styles/AccountSettings.css";

export function AccountSettings() {
  const navigate = useNavigate();
  const {
    userProfile,
    loading,
    error,
    loadUserProfile,
    updateUserProfile,
    updateEmail,
    unlinkProvider,
    downloadAccountData,
  } = useUserProfile();

  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [accountStats, setAccountStats] = useState<{
    walletItemsCount: number;
    transactionsCount: number;
  } | null>(null);

  // Load profile on mount
  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  // Load account statistics
  useEffect(() => {
    const loadStats = async () => {
      if (!auth.currentUser) return;

      try {
        const statsRef = doc(db, "users", auth.currentUser.uid);
        const statsDoc = await getDoc(statsRef);

        if (statsDoc.exists()) {
          const data = statsDoc.data();
          setAccountStats({
            walletItemsCount: data.walletItemsCount || 0,
            transactionsCount: data.transactionsCount || 0,
          });
        }
      } catch (err) {
        console.error("Failed to load account statistics:", err);
      }
    };

    loadStats();
  }, []);

  // Populate edit mode fields
  useEffect(() => {
    if (userProfile && editMode) {
      setDisplayName(userProfile.displayName || "");
      setPhotoURL(userProfile.photoURL || "");
    }
  }, [editMode, userProfile]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    try {
      await updateUserProfile({
        displayName: displayName || "",
        photoURL: photoURL || "",
      });

      setMessage({ type: "success", text: "Profile updated successfully!" });
      setEditMode(false);
    } catch (err: any) {
      setMessage({ type: "error", text: err.message || "Failed to update profile" });
    }
  };

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!newEmail) {
      setMessage({ type: "error", text: "Please enter a new email" });
      return;
    }

    try {
      const result = await updateEmail(newEmail);
      setMessage({
        type: "success",
        text: result.message || "Email verification sent",
      });
      setNewEmail("");
    } catch (err: any) {
      setMessage({ type: "error", text: err.message || "Failed to update email" });
    }
  };

  const handleUnlinkProvider = async (providerId: string) => {
    if (window.confirm(`Unlink ${providerId} from your account?`)) {
      setMessage(null);
      try {
        await unlinkProvider(providerId);
        setMessage({ type: "success", text: `${providerId} unlinked successfully` });
      } catch (err: any) {
        setMessage({ type: "error", text: err.message || "Failed to unlink provider" });
      }
    }
  };

  const handleDownloadData = async () => {
    setMessage(null);
    try {
      await downloadAccountData();
      setMessage({ type: "success", text: "Account data downloaded" });
    } catch (err: any) {
      setMessage({ type: "error", text: err.message || "Failed to download data" });
    }
  };

  const handleSignOut = async () => {
    try {
      await firebaseSignOut(auth);
      navigate("/login");
    } catch (err) {
      setMessage({ type: "error", text: "Failed to sign out" });
    }
  };

  const getProviderIcon = (providerId: string) => {
    switch (providerId) {
      case "google.com":
        return "🔍";
      case "password":
        return "🔐";
      default:
        return "🔗";
    }
  };

  const getProviderLabel = (providerId: string) => {
    switch (providerId) {
      case "google.com":
        return "Google";
      case "password":
        return "Email/Password";
      default:
        return providerId;
    }
  };

  if (loading && !userProfile) {
    return (
      <div className="account-settings-container">
        <div className="loading">Loading account settings...</div>
      </div>
    );
  }

  return (
    <div className="account-settings-container">
      <div className="settings-header">
        <h1>⚙️ Account Settings</h1>
        <p className="subtitle">Manage your profile, security, and account data</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.type === "success" ? "✓" : "✕"} {message.text}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          ✕ Error: {error.message}
        </div>
      )}

      {/* Profile Information Section */}
      <div className="settings-section">
        <div className="section-header">
          <h2>👤 Profile Information</h2>
          {!editMode && (
            <button className="btn-edit" onClick={() => setEditMode(true)}>
              ✎ Edit Profile
            </button>
          )}
        </div>

        {!editMode ? (
          <div className="profile-display">
            {userProfile?.photoURL && (
              <img src={userProfile.photoURL} alt="Profile" className="profile-avatar" />
            )}
            <div className="profile-info">
              <div className="info-row">
                <label>Name:</label>
                <span>{userProfile?.displayName || "Not set"}</span>
              </div>
              <div className="info-row">
                <label>Email:</label>
                <span>{userProfile?.email}</span>
              </div>
              <div className="info-row">
                <label>Account Created:</label>
                <span>
                  {userProfile?.createdAt
                    ? new Date(userProfile.createdAt).toLocaleDateString()
                    : "Unknown"}
                </span>
              </div>
              <div className="info-row">
                <label>Last Sign In:</label>
                <span>
                  {userProfile?.lastSignInTime
                    ? new Date(userProfile.lastSignInTime).toLocaleDateString()
                    : "Unknown"}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdateProfile} className="profile-edit-form">
            <div className="form-group">
              <label htmlFor="displayName">Display Name:</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="photoURL">Photo URL:</label>
              <input
                id="photoURL"
                type="url"
                value={photoURL}
                onChange={(e) => setPhotoURL(e.target.value)}
                placeholder="https://example.com/photo.jpg"
              />
              {photoURL && (
                <div className="photo-preview">
                  <img src={photoURL} alt="Preview" style={{ maxWidth: "100px", borderRadius: "8px" }} />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save" disabled={loading}>
                {loading ? "Saving..." : "✓ Save Changes"}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setEditMode(false)}
                disabled={loading}
              >
                ✕ Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Account Statistics Section */}
      {accountStats && (
        <div className="settings-section">
          <h2>📊 Account Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{accountStats.walletItemsCount}</div>
              <div className="stat-label">Wallet Items</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{accountStats.transactionsCount}</div>
              <div className="stat-label">Transactions</div>
            </div>
          </div>
        </div>
      )}

      {/* Email Management Section */}
      <div className="settings-section">
        <h2>✉️ Email Management</h2>
        <div className="email-info">
          <p>Current email: <strong>{userProfile?.email}</strong></p>
          <p className="text-muted">To change your email, enter a new one below. We'll send a verification link.</p>
        </div>

        <form onSubmit={handleUpdateEmail} className="email-form">
          <div className="form-group">
            <label htmlFor="newEmail">New Email:</label>
            <input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new.email@example.com"
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading || !newEmail}>
            {loading ? "Sending..." : "📧 Send Verification Link"}
          </button>
        </form>
      </div>

      {/* OAuth Providers Section */}
      <div className="settings-section">
        <h2>🔗 Connected Accounts</h2>
        <div className="providers-list">
          {userProfile?.providers && userProfile.providers.length > 0 ? (
            userProfile.providers.map((provider) => (
              <div key={provider} className="provider-item">
                <span className="provider-icon">{getProviderIcon(provider)}</span>
                <span className="provider-name">{getProviderLabel(provider)}</span>
                {userProfile.providers.length > 1 && (
                  <button
                    className="btn-unlink"
                    onClick={() => handleUnlinkProvider(provider)}
                    disabled={loading}
                  >
                    Unlink
                  </button>
                )}
                {userProfile.providers.length === 1 && (
                  <span className="text-muted">Required (at least one provider needed)</span>
                )}
              </div>
            ))
          ) : (
            <p>No providers connected</p>
          )}
        </div>
      </div>

      {/* Data Management Section */}
      <div className="settings-section">
        <h2>💾 Data Management</h2>
        <p className="text-muted">Download a copy of your account data in JSON format.</p>
        <button className="btn-secondary" onClick={handleDownloadData} disabled={loading}>
          ⬇️ Download My Data
        </button>
      </div>

      {/* Danger Zone */}
      <div className="settings-section danger-zone">
        <h2>⚠️ Danger Zone</h2>
        <button className="btn-danger" onClick={handleSignOut}>
          🚪 Sign Out
        </button>
        <p className="text-muted">You will be redirected to the login page.</p>
      </div>
    </div>
  );
}
